import { Immutable } from "immer";
import React from "react";
import { Image } from "tsi-common-react/src/components/Image/index";
import { Link } from "tsi-common-react/src/components/Link/index";
import { RatingGraphic } from "tsi-common-react/src/components/RatingGraphic/index";
import { SVGIcon } from "tsi-common-react/src/components/SVGIcon/index";
import { TempurLoveBreadcrumbs } from "tsi-common-react/src/components/TempurLoveBreadcrumbs/index";
import {
    ContentBlock,
    SpacingBlock,
    toBodyBlocks,
} from "tsi-common-react/src/components/TempurLoveStreamfieldBlocks/index";
import { UGCApiGrid } from "tsi-common-react/src/components/UGCApiGrid/index";
import { isoWebPageURL } from "tsi-common-react/src/models/nominals";
import {
    getTileImage,
    getTileTitle,
} from "tsi-common-react/src/utils/tempurLoveGrid";
import { t } from "ttag";

import { Context, templates } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { CustomerContentHubBlogTile } from "../CustomerContentHubBlogTile";
import { CustomerContentHubSocialIcons } from "../CustomerContentHubSocialIcons";

// The story body is composed inside the live
// CustomerContentHubStoryPageDocumentTemplate, which spreads its own props down
// and narrows love_root to non-null (story pages only exist under a
// CustomerContentHubRootPage); derive the shape from it so the two stay in sync.
export type ICustomerContentHubStoryPageProps = Immutable<
    Omit<
        templates.CustomerContentHubStoryPageDocumentTemplate,
        "is_preview" | "page_chrome" | "love_root"
    > & {
        // The body only renders love_root's url + title (the header owns the
        // rest); mirror the narrow pick the old island template carried.
        love_root: Pick<
            NonNullable<
                templates.CustomerContentHubStoryPageDocumentTemplate["love_root"]
            >,
            "url" | "title"
        >;
    }
>;

type IProps = ICustomerContentHubStoryPageProps;

export const CustomerContentHubStoryPage = (
    props: IProps,
): React.JSX.Element => {
    const context = React.useContext(Context);
    const locale = context.i18n.language.code || "en-US";
    const formattedDate = new Date(props.page.date).toLocaleDateString(locale, {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
    // Build breadcrumb items
    const breadcrumbItems = [
        { title: props.love_root.title, url: props.love_root.url || "/" },
        ...(props.first_category
            ? [
                  {
                      title: props.first_category.name,
                      url: `${props.love_root.url}c/${encodeURIComponent(props.first_category.slug)}/`,
                  },
              ]
            : []),
        { title: props.page.title.substring(0, 25), isCurrentPage: true },
    ];

    return (
        <>
            {/* Breadcrumbs */}
            <TempurLoveBreadcrumbs
                className="al-tlovestory-breadcrumbs"
                items={breadcrumbItems}
                useCappedWidth={true}
            >
                <div className="sub-nav__links">
                    {props.previous_page ? (
                        <Link
                            className="sub-nav__link al-tempur-love-story--previous"
                            href={isoWebPageURL.wrap(
                                props.previous_page.url || "/",
                            )}
                        >
                            <SVGIcon name="lessThanSign" className="preNext" />
                            <span className="text">{t`Previous`}</span>
                        </Link>
                    ) : (
                        <span className="sub-nav__link al-tempur-love-story--previous">
                            <SVGIcon name="lessThanSign" className="preNext" />
                            <span className="text">{t`Previous`}</span>
                        </span>
                    )}
                    <span className="sub-nav__divider" />
                    {props.next_page ? (
                        <Link
                            className="sub-nav__link al-tempur-love-story--next"
                            href={isoWebPageURL.wrap(
                                props.next_page.url || "/",
                            )}
                        >
                            <span className="text">{t`Next`}</span>
                            <SVGIcon
                                name="greaterThanSign"
                                className="preNext"
                            />
                        </Link>
                    ) : (
                        <span className="sub-nav__link al-tempur-love-story--next">
                            <span className="text">{t`Next`}</span>
                            <SVGIcon
                                name="greaterThanSign"
                                className="preNext"
                            />
                        </span>
                    )}
                </div>
            </TempurLoveBreadcrumbs>

            {/* TODO: header_blocks (Toolbox StreamField) not included in SSR.
                Not actively used on Story pages. Implementing all Toolbox block
                types (~20+ per site) would be inefficient. If needed, implement
                only used blocks in a separate ticket. */}

            {/* Main Content */}
            <div className="l-full-width">
                <div className="l-capped-width blog-container">
                    <article className="blog">
                        <section className="blog__main-intro" id="read-more">
                            <h2>{props.page.title}</h2>
                            <div className="blog__avatar">
                                {props.page.avatar && (
                                    <figure className="avatar">
                                        <img
                                            className="avatar__image"
                                            src={props.page.avatar.block.url}
                                            alt={
                                                props.page.avatar.block.title ||
                                                props.page.author
                                            }
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </figure>
                                )}
                                <p>
                                    {t`— by ${props.page.author} on ${formattedDate}`}
                                </p>
                            </div>
                        </section>

                        <section className="blog__main-article">
                            {/* Body Content */}
                            {props.page.body && (
                                <StreamField
                                    tagName={React.Fragment}
                                    value={toBodyBlocks(props.page.body.value)}
                                    components={{
                                        content: ContentBlock,
                                        spacing: SpacingBlock,
                                    }}
                                />
                            )}

                            {/* Social Icons */}
                            <CustomerContentHubSocialIcons
                                categories={props.categories}
                                loveRootUrl={props.love_root.url || "/"}
                                city={props.page.city}
                                state={props.page.state}
                                pageUrl={props.page_url}
                                tileImageUrl={props.tile_image_url}
                            />

                            {/* Featured Products */}
                            {props.featured_products.length > 0 && (
                                <div className="blog__featured-products">
                                    <h4 className="blog__featured-products-title">
                                        <span className="flourish">{t`Featured`}</span>{" "}
                                        {t`Products`}
                                    </h4>
                                    <section className="ugc-recommends al-tlovestory-featuredproducts">
                                        <div className="ugc-recommends__products">
                                            {props.featured_products.map(
                                                (product) => (
                                                    <a
                                                        key={product.url}
                                                        className="ugc-recommends-product"
                                                        href={product.url}
                                                        target="_blank"
                                                        title={product.title}
                                                        rel="noreferrer"
                                                    >
                                                        {product.image_url && (
                                                            <Image
                                                                className="ugc-recommends-product__image"
                                                                src={
                                                                    product.image_url
                                                                }
                                                                alt=""
                                                            />
                                                        )}
                                                        <div>
                                                            <div className="ugc-recommends-product__title">
                                                                {product.title}
                                                            </div>
                                                            <RatingGraphic
                                                                rating={
                                                                    product.rating
                                                                }
                                                                numReviews={
                                                                    product.num_reviews
                                                                }
                                                                cardClass="ugc-recommends-product"
                                                                cardSize="small"
                                                            />
                                                        </div>
                                                    </a>
                                                ),
                                            )}
                                        </div>
                                    </section>
                                </div>
                            )}
                        </section>

                        <aside className="blog__aside">
                            {/* Next Story */}
                            {props.next_page && (
                                <section className="blog-post al-tlovestory-nexttile">
                                    <h2>{t`Next`}</h2>
                                    <CustomerContentHubBlogTile
                                        tileImage={props.next_page.tile_image}
                                        tileTitle={getTileTitle(
                                            props.next_page,
                                            props.page.title,
                                        )}
                                        tileBody={
                                            props.next_page_tile_body || ""
                                        }
                                        url={props.next_page.url || "/"}
                                        categories={
                                            props.next_page_categories || []
                                        }
                                        loveRootUrl={props.love_root.url || "/"}
                                    />
                                </section>
                            )}
                            {/* Related Pages */}
                            {props.related_pages.length > 0 && (
                                <section className="blog-post">
                                    <h2>{t`Related`}</h2>
                                    {props.related_pages.map(
                                        (
                                            relatedPage: IProps["related_pages"][number],
                                            index: number,
                                        ) => (
                                            <div key={relatedPage.url || index}>
                                                <CustomerContentHubBlogTile
                                                    tileImage={getTileImage(
                                                        relatedPage,
                                                    )}
                                                    tileTitle={getTileTitle(
                                                        relatedPage,
                                                        "",
                                                    )}
                                                    tileBody={
                                                        props
                                                            .related_pages_tile_bodies[
                                                            index
                                                        ] || ""
                                                    }
                                                    url={relatedPage.url || "/"}
                                                    categories={
                                                        props
                                                            .related_pages_categories[
                                                            index
                                                        ] || []
                                                    }
                                                    loveRootUrl={
                                                        props.love_root.url ||
                                                        "/"
                                                    }
                                                />
                                            </div>
                                        ),
                                    )}
                                </section>
                            )}
                            {/* UGC Gallery - SSR */}
                            <article className="tempurlove-gallery">
                                <h5 className="tempurlove-gallery__title">
                                    {props.social_header}
                                </h5>
                                <div className="tempurlove-gallery__grid al-tlovestory-ugcgrid">
                                    <UGCApiGrid
                                        pageSize={6}
                                        random={true}
                                        initialGridData={props.initial_ugc_data}
                                        initialNextUrl={props.initial_next_url}
                                    />
                                </div>
                                {props.social_page_url && (
                                    <a
                                        className="tempurlove-gallery__link"
                                        href={props.social_page_url}
                                    >
                                        {t`View More`}
                                    </a>
                                )}
                            </article>
                        </aside>
                    </article>

                    {/* TODO: footer_blocks (Toolbox StreamField) not included in SSR.
                        Not actively used on Story pages. Implementing all Toolbox block
                        types (~20+ per site) would be inefficient. If needed, implement
                        only used blocks in a separate ticket. */}
                </div>
            </div>
        </>
    );
};
